import React, { FC, useEffect } from 'react';
import { create } from 'zustand';

export interface LeftMatchItem {
  label: string;
  pairId: string;
  show?: boolean;
  image?: any;
}

export interface RightMatchItem {
  label: string;
  pairId: string;
  leftItem?: LeftMatchItem;
  show?: boolean;
  image?: any;
}

export interface QuizMatchStore {
  leftItems: LeftMatchItem[];
  rightItems: RightMatchItem[];
  match: (id: string, item?: LeftMatchItem | undefined) => void;
  unSelect: (id: string, item?: LeftMatchItem | undefined, reset?: boolean) => void;
  mobileMatch: (left: LeftMatchItem, right: RightMatchItem) => void;
}

const _useQuizMatchStore = create<QuizMatchStore>(
  ((set, get) => ({
      leftItems: [] as LeftMatchItem[],
      rightItems: [] as RightMatchItem[],
      mobileMatch: (left, right) => {
        const leftItems = get().leftItems as LeftMatchItem[];
        const rightItems = get().rightItems as RightMatchItem[];

        rightItems.forEach((el) => {
          if (el?.leftItem?.pairId === left.pairId) {
            delete el.leftItem;
          }

          if (el.pairId === right.pairId) {
            if (el.leftItem) {
              leftItems.forEach((i) => {
                if (i.pairId === el.leftItem?.pairId) {
                  i.show = true;
                }
              });
            }
            el.leftItem = left;
          }
        });

        leftItems.forEach((el) => {
          if (el.pairId === left.pairId) {
            el.show = false;
          }
        });

        set({ leftItems, rightItems });
      },
      unSelect: (id, item, reset) => {
        if (id && item) {
          const leftItems = get().leftItems as LeftMatchItem[];
          leftItems.push(item);

          let unique = reset
            ? [...new Set(leftItems.map((item) => item))]
            : leftItems;

          if (reset) {
            unique = unique.map((el) => {
              if (el.pairId === item.pairId) {
                el.show = true;
              }
              return el;
            });
          }

          set((s) => ({
            leftItems: unique,
            rightItems: s.rightItems.map((i) => {
              if (i.leftItem?.pairId === item?.pairId) {
                return { ...i, leftItem: undefined };
              }
              return i;
            }) as RightMatchItem[],
          }));
        }
      },
      match: (id, item) =>
        set((s) => ({
          leftItems: s.leftItems
            .concat([
              (s.rightItems.find((i) => i.pairId === id)
                ?.leftItem as LeftMatchItem) as LeftMatchItem
            ])
            .filter((i) => i && i.pairId !== item?.pairId),
          rightItems: s.rightItems.map((i) => {
            if (i.pairId === id) {
              return { ...i, leftItem: item };
            }
            if (i.leftItem?.pairId === item?.pairId) {
              return { ...i, leftItem: undefined };
            }
            return i;
          }) as RightMatchItem[],
        })),
    })
  )
);

export const QuizMatchProvider: FC<{
  leftItems: LeftMatchItem[];
  rightItems: RightMatchItem[];
}> = ({ children, leftItems, rightItems }) => {
  useEffect(() => {
    _useQuizMatchStore.setState({ leftItems, rightItems });
  }, [leftItems, rightItems]);

  return <>{children}</>;
};

export const useQuizMatchStore = _useQuizMatchStore;
